export type PayoffGraphMetaData = {
    time: number
    callOi: number
    putOi: number
    callDelta: number
    putDelta: number
    expiryPnl: number
    expectedPnl: number
}

type Listener = () => void

const metaByKey: Record<string, PayoffGraphMetaData | null | undefined> = {}
const listenersByKey: Record<string, Set<Listener>> = {}

export const PAYOFF_GRAPH_META_KEY = 'payoff-graph'
export const PAYOFF_CHART_META_KEY = 'payoff-chart'

export const payoffGraphMetaDataEquals = (
    a: PayoffGraphMetaData | null | undefined,
    b: PayoffGraphMetaData | null | undefined
) => {
    if (a === b) return true
    if (!a || !b) return false
    return (
        a.time === b.time &&
        a.callOi === b.callOi &&
        a.putOi === b.putOi &&
        a.callDelta === b.callDelta &&
        a.putDelta === b.putDelta &&
        a.expiryPnl === b.expiryPnl &&
        a.expectedPnl === b.expectedPnl
    )
}

const notify = (key: string) => {
    listenersByKey[key]?.forEach((listener) => listener())
}

export const getPayoffGraphMetaData = (key: string): PayoffGraphMetaData | null => {
    return metaByKey[key] ?? null
}

export const setPayoffGraphMetaData = (key: string, meta: PayoffGraphMetaData | null) => {
    if (payoffGraphMetaDataEquals(metaByKey[key] ?? null, meta)) return
    metaByKey[key] = meta
    notify(key)
}

export const subscribePayoffGraphMetaData = (key: string, listener: Listener) => {
    if (!listenersByKey[key]) {
        listenersByKey[key] = new Set()
    }
    listenersByKey[key].add(listener)
    return () => {
        listenersByKey[key]?.delete(listener)
    }
}

export const clearPayoffGraphMetaData = (key: string) => {
    if (metaByKey[key] === undefined) return
    delete metaByKey[key]
    notify(key)
}

export type PayoffSnapshotLike = {
    time: number | undefined
    oiData: [number, number]
    deltaData: [number, number]
    pnlData?: { value: number }
    tradingDayPnlData?: { value: number }
}

export const payoffMetaFromSnapshot = (snapshot: PayoffSnapshotLike | null | undefined): PayoffGraphMetaData | null => {
    if (!snapshot || snapshot.time === undefined) return null
    return {
        time: snapshot.time,
        callOi: snapshot.oiData[0],
        putOi: snapshot.oiData[1],
        callDelta: snapshot.deltaData[0],
        putDelta: snapshot.deltaData[1],
        expiryPnl: snapshot.pnlData?.value ?? NaN,
        expectedPnl: snapshot.tradingDayPnlData?.value ?? NaN
    }
}
